import WireframeLayout, { buildSidebarSections } from '@/components/WireframeLayout'
import { cn, Badge, Card, Button, Progress, KPI } from '../components/primitives'
import { SKU_DATA } from '../components/data'

function MarketingView() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3 flex-wrap">
        <KPI label="Ad Spend (MTD)" value="$48.2K" delta="+12% vs plan" dir="up" />
        <KPI label="ROAS" value="3.8x" delta="+0.4 vs target" dir="up" />
        <KPI label="CAC" value="$14.20" delta="-8% vs last month" dir="up" />
        <KPI label="Inventory-Aligned" value="82%" sub="% of spend on stocked SKUs" />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-1">AI Recommends: Push These SKUs</h3>
        <p className="text-xs text-slate-500 mb-3">Excess stock SKUs that benefit from demand acceleration</p>
        <div className="grid grid-cols-2 gap-3">
          {SKU_DATA.filter(s => s.status === "overstock" || s.dos > 80).map(s => (
            <Card key={s.id} className="border-violet-200">
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div><div className="text-sm font-semibold text-slate-900">{s.name}</div><div className="text-xs text-slate-400">{s.id} · {s.ch}</div></div>
                  <Badge variant={s.status}>{s.status}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div><div className="text-xs text-slate-400">Excess</div><div className="text-sm font-bold text-violet-600">{(s.stock - s.velocity * 45).toLocaleString()}</div></div>
                  <div><div className="text-xs text-slate-400">Capital</div><div className="text-sm font-bold text-amber-600">${((s.stock - s.velocity * 45) * 4.2 / 1000).toFixed(1)}K</div></div>
                  <div><div className="text-xs text-slate-400">Margin</div><div className="text-sm font-bold text-emerald-600">{(s.margin * 100).toFixed(0)}%</div></div>
                </div>
                <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-600 leading-relaxed mb-3">
                  💡 <span className="text-emerald-700 font-semibold">AI:</span> Allocate ${((s.stock - s.velocity * 45) * 0.15).toFixed(0)} ad spend → sell-through ~{Math.round(s.dos * 0.3)} days faster. ROAS: {(3.2 + Math.random() * 1.5).toFixed(1)}x
                </div>
                <Button variant="violet" size="sm">Create Campaign →</Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Campaign–Replenishment Timing</h3>
        <Card className="p-5">
          <div className="flex gap-0.5 mb-4">
            {Array.from({ length: 30 }, (_, i) => {
              const d = i + 1, camp = (d >= 3 && d <= 5) || (d >= 15 && d <= 17) || (d >= 20 && d <= 31), repl = [8, 12, 15, 18].includes(d);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-slate-300" style={{ fontSize: 8 }}>{d}</span>
                  <div className={cn("w-full h-6 rounded-sm", camp ? "bg-blue-100 border border-blue-300" : "bg-slate-50")} />
                  {repl && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                </div>
              );
            })}
          </div>
          <div className="flex gap-5 text-xs text-slate-500">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-300" /> Campaign</div>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Replenishment</div>
          </div>
        </Card>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Margin Protection Monitor</h3>
        <Card className="divide-y divide-slate-50">
          {[
            { sku: "SKU-001", name: "Vitamin C Serum", bm: 62, em: 58, spend: 2400, risk: "low" },
            { sku: "SKU-002", name: "HA Toner", bm: 58, em: 52, spend: 3100, risk: "medium" },
            { sku: "SKU-003", name: "SPF50 Sunscreen", bm: 55, em: 41, spend: 4200, risk: "high" },
            { sku: "SKU-005", name: "Niacinamide Serum", bm: 61, em: 56, spend: 2800, risk: "low" },
          ].map((it, i) => (
            <div key={i} className="p-4 flex justify-between items-center">
              <div><div className="text-sm font-semibold text-slate-900">{it.name}</div><div className="text-xs text-slate-400">{it.sku} · ${it.spend.toLocaleString()}/mo</div></div>
              <div className="flex items-center gap-4">
                <div className="text-xs"><span className="text-emerald-600 font-semibold">{it.bm}%</span><span className="text-slate-400"> → </span><span className={cn("font-semibold", it.risk === "high" ? "text-red-600" : it.risk === "medium" ? "text-amber-600" : "text-emerald-600")}>{it.em}%</span></div>
                <div className="w-20"><Progress value={it.em} max={it.bm} color={it.risk === "high" ? "#ef4444" : it.risk === "medium" ? "#f59e0b" : "#10b981"} /></div>
                {it.risk === "high" && <span className="text-xs text-red-600 font-semibold">⚠ Destruction</span>}
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard({ role }: { role: string }) {
  return (
    <WireframeLayout
      title="Performance Marketing Hub"
      subtitle="Inventory-aware demand acceleration"
      titleIcon="▲"
      titleColor="#8b5cf6"
      sidebarLinks={buildSidebarSections(role, "dashboard")}
    >
      <MarketingView />
    </WireframeLayout>
  )
}
